import { Building2, ChevronDown, ChevronRight, CircleCheck, Cloud, Download, Globe, Laptop, Send, ShieldCheck, TriangleAlert } from '@orgus/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { OS_LABEL, PAIR_STATE_MESSAGE, RELEASE_TARGET_LABEL } from '@orgus/protocol';
import type { AgentDownload, AgentDownloads, PairRequestView, ReleaseTarget } from '@orgus/protocol';
import {
  BUTTON_SIZE,
  BUTTON_VARIANT,
  Button,
  Checkbox,
  CopyButton,
  Dialog,
  ErrorState,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RowStatus,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  cn,
  formatBytes,
} from '@orgus/ui';
import { useNavigate } from 'react-router';
import { useOrgus } from '../../lib/store.ts';
import { NARROW_QUERY } from '../../lib/useMediaQuery.ts';
import { PAIR_DIFFERENT_NETWORK, PairRequestFacts } from '../pairing/PairRequestFacts.tsx';
import { DeveloperJoin } from './DeveloperJoin.tsx';
import { ReadinessFound } from './Readiness.tsx';
import { CloudVmTab } from './CloudVmTab.tsx';
import { JoinLinkPanel } from './JoinLinkPanel.tsx';
import { joinTicketsOn } from './joinTickets.ts';
import { useJoinTickets } from './useJoinTickets.ts';
import { IT_DEPLOYMENT_DOC, allInstallCommands, bareDomain, detectPlatform, downloadLabel, downloadPageUrl, mailtoDownload, pairCommand, pickDownloads, terminalInstallCommand } from './download-model.ts';
import type { Platform } from './download-model.ts';
import { consoleReach } from './pairing.ts';
import type { AddMachinePreset, ConsoleReach } from './pairing.ts';
import { usePairing } from './usePairing.ts';
import type { PairingState } from './usePairing.ts';
import { useDownloads } from './useDownloads.ts';

/** This browser's computer, read once per open (with the architecture hint when the browser gives it). */
function usePlatform(open: boolean): Platform {
  const read = (architecture?: string): Platform => {
    const nav = navigator as Navigator & { userAgentData?: { platform?: string; mobile?: boolean } };
    return detectPlatform({
      userAgent: navigator.userAgent,
      ...(nav.userAgentData?.platform ? { platform: nav.userAgentData.platform } : {}),
      ...(nav.userAgentData?.mobile !== undefined ? { mobile: nav.userAgentData.mobile } : {}),
      ...(architecture ? { architecture } : {}),
      narrow: typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches,
    });
  };
  const [platform, setPlatform] = useState<Platform>(() => read());
  useEffect(() => {
    if (!open) return;
    setPlatform(read());
    const nav = navigator as Navigator & { userAgentData?: { getHighEntropyValues?: (h: string[]) => Promise<{ architecture?: string }> } };
    let live = true;
    nav.userAgentData
      ?.getHighEntropyValues?.(['architecture'])
      .then((v) => {
        if (live && v.architecture) setPlatform(read(v.architecture));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [open]);
  return platform;
}

/** One fact with its icon: "Signed by Orgus". */
function Fact({ icon, children, tone = 'neutral', hint }: { icon: ReactNode; children: ReactNode; tone?: 'neutral' | 'attention'; hint?: string }) {
  const body = (
    <span className={cn('inline-flex min-w-0 items-center gap-2 text-caption', tone === 'attention' ? 'text-status-attention-ink' : 'text-fg-muted')} tabIndex={hint ? 0 : undefined}>
      <span className={cn('shrink-0 [&_svg]:size-4', tone === 'attention' ? 'text-status-attention' : 'text-fg-subtle')} aria-hidden>
        {icon}
      </span>
      <span className="truncate">{children}</span>
    </span>
  );
  return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

/**
 * A link styled as a button that downloads the file; in demo mode it only says what it would do.
 * `onPress` (join links on) runs beside the download, never instead of it: a failed join link
 * shows inline and the file still comes.
 */
function DownloadLink({ d, primary, onChoose, onPress }: { d: AgentDownload; primary: boolean; onChoose: (t: ReleaseTarget) => void; onPress?: () => void }) {
  const mock = useOrgus((s) => s.mode === 'mock');
  return (
    <a
      href={`/api/fleet/downloads/${d.target}`}
      download={d.file}
      onClick={(e) => {
        onChoose(d.target);
        onPress?.();
        if (mock) e.preventDefault();
      }}
      className={cn(BUTTON_VARIANT[primary ? 'primary' : 'secondary'], BUTTON_SIZE.lg, 'w-full sm:w-auto')}
      data-testid="download-primary"
    >
      <Download aria-hidden />
      {downloadLabel(d.target)}
    </a>
  );
}

/** Send the page to a computer: the share sheet where there is one, else an email to yourself. */
async function sendLink(): Promise<void> {
  const url = downloadPageUrl(window.location.origin);
  const nav = navigator as Navigator & { share?: (d: { title: string; text: string; url: string }) => Promise<void> };
  if (nav.share) {
    try {
      await nav.share({ title: 'Add a computer to Orgus', text: 'Open this on the computer you want to add.', url });
      return;
    } catch (e) {
      // Closing the share sheet is not a failure.
      if (e instanceof DOMException && e.name === 'AbortError') return;
    }
  }
  window.location.href = mailtoDownload(window.location.origin);
}

/** A command in mono with its Copy button, as the pair command shows it. */
function CommandBox({ command, label }: { command: string; label: string }) {
  return (
    <div className="flex items-start gap-2 rounded-card border border-border bg-bg py-2 pl-3 pr-1">
      <code className="min-w-0 flex-1 select-all break-words py-1 font-mono text-caption text-fg" aria-label={label}>
        {command}
      </code>
      <CopyButton text={command} label="Copy command" size="sm" />
    </div>
  );
}

/**
 * The one-line install next to the download (ADR 0100): the same signed agent, fetched and
 * checked by a script from the fixed release origin. PowerShell on Windows, sh elsewhere.
 */
export function TerminalInstall({ target, hubUrl }: { target: ReleaseTarget; hubUrl?: string }) {
  const command = terminalInstallCommand({ target, ...(hubUrl ? { hubUrl } : {}) });
  if (!command) return null;
  return (
    <div className="flex flex-col gap-2" data-testid="install-oneliner">
      <p className="text-caption text-fg-subtle">Or in a terminal:</p>
      <CommandBox command={command} label={`One-line install for ${RELEASE_TARGET_LABEL[target]}`} />
    </div>
  );
}

/** Every one-line install, in the command-line disclosure: macOS and Linux, PowerShell, CMD. */
export function InstallCommands({ hubUrl }: { hubUrl?: string }) {
  const all = allInstallCommands(hubUrl);
  if (all.length === 0) return null;
  return (
    <section aria-label="One-line install" className="flex flex-col gap-3" data-testid="install-commands">
      {all.map((c) => (
        <div key={c.shell} className="flex flex-col gap-1">
          <p className="text-caption text-fg-subtle">{c.label}</p>
          <CommandBox command={c.command} label={`One-line install, ${c.label}`} />
        </div>
      ))}
    </section>
  );
}

function Downloads({ data, platform, secondary, onChoose, onPrimary }: { data: AgentDownloads; platform: Platform; secondary: boolean; onChoose: (t: ReleaseTarget) => void; onPrimary?: () => void }) {
  if (data.unavailable) {
    return (
      <div role="alert" className="flex gap-3 rounded-card border border-status-attention-line bg-status-attention-soft px-3 py-3 text-ui" data-testid="download-unavailable">
        <TriangleAlert className="mt-1 size-4 shrink-0 text-status-attention" aria-hidden />
        <p className="min-w-0 flex-1 text-fg-muted">
          <span className="font-medium text-status-attention-ink">{data.unavailable.what}:</span> {data.unavailable.why} {data.unavailable.next}
        </p>
      </div>
    );
  }
  const mock = useOrgus((s) => s.mode === 'mock');
  const { primary, others } = pickDownloads(data.downloads, platform);
  const domain = bareDomain(data.server.domain);
  return (
    <div className="flex flex-col gap-4">
      {platform.kind === 'phone' ? (
        <div className="flex flex-col gap-2">
          <Button variant={secondary ? 'secondary' : 'primary'} size="lg" icon={<Send />} className="w-full" onClick={() => void sendLink()}>
            Send download link to my computer
          </Button>
          <p className="text-caption text-fg-subtle">Open the link on a Windows, macOS or Linux computer.</p>
        </div>
      ) : primary ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <DownloadLink d={primary} primary={!secondary} onChoose={onChoose} onPress={onPrimary} />
          <span className="text-caption text-fg-subtle num">
            Version {data.version} · {formatBytes(primary.size)}
          </span>
        </div>
      ) : (
        <p className="text-ui text-fg-muted">Pick the system of the computer you want to add.</p>
      )}

      {platform.kind === 'computer' && primary && <TerminalInstall target={primary.target} {...(data.pairWith ? { hubUrl: data.pairWith.url } : {})} />}

      {others.length > 0 && (
        <nav aria-label="Other systems" className="flex flex-wrap items-center gap-x-3 gap-y-1 text-caption">
          <span className="text-fg-subtle">{primary ? 'Other systems' : 'Download for'}</span>
          {others.map((d) => (
            <a
              key={d.target}
              href={`/api/fleet/downloads/${d.target}`}
              download={d.file}
              onClick={(e) => {
                onChoose(d.target);
                if (mock) e.preventDefault();
              }}
              className="text-fg-muted underline-offset-2 hover:text-fg hover:underline"
            >
              {RELEASE_TARGET_LABEL[d.target]}
            </a>
          ))}
        </nav>
      )}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border-subtle pt-3">
        {data.signature?.test ? (
          <Fact icon={<TriangleAlert />} tone="attention" hint="Only for test builds of the agent, never for real machines.">
            Signed with a TEST key
          </Fact>
        ) : (
          <Fact icon={<ShieldCheck />} hint={data.signature ? `Release key ${data.signature.keyId}. The agent checks every update against it.` : undefined}>
            Signed by {data.publisher}
          </Fact>
        )}
        {data.server.reachable ? (
          <Fact icon={<Globe />}>Connects to {domain}</Fact>
        ) : (
          <Fact icon={<TriangleAlert />} tone="attention" hint={data.server.why}>
            Only this computer can reach {domain}
          </Fact>
        )}
      </div>
    </div>
  );
}

/**
 * A local hub (AgentDownloads.pairWith): the downloaded file has no address built in, so the one
 * step after the download is this command on the new computer. It follows the file the person
 * picked; "No browser there" (a server or VM) makes it show three words to type instead.
 */
function PairCommand({ d, url }: { d: AgentDownload; url: string }) {
  const [words, setWords] = useState(false);
  const command = pairCommand({ file: d.file, target: d.target, url, words });
  return (
    <div className="flex flex-col gap-2" data-testid="pair-command">
      <p className="text-caption text-fg-subtle">{d.target.startsWith('windows') ? 'Then run in PowerShell, in the download folder' : 'Then run in a terminal, in the download folder'}</p>
      <CommandBox command={command} label={`Pair command for ${RELEASE_TARGET_LABEL[d.target]}`} />
      <Checkbox checked={words} onCheckedChange={setWords} label="No browser there" hint="A server or VM shows three words to type instead." />
    </div>
  );
}

const ENDED_LABEL: Partial<Record<PairRequestView['state'], string>> = {
  denied: 'Declined',
  expired: 'Request expired',
  conflict: 'Stopped for safety',
  cancelled: 'Cancelled on the computer',
};

/** What happens after the download: waiting, approve in place, joining, ended. */
function Live({ pairing, reach, local, byLink = false }: { pairing: PairingState; reach: ConsoleReach; local: boolean; byLink?: boolean }) {
  const p = pairing.phase;
  if (p.kind === 'approve') {
    const r = p.request;
    const name = r.machine.hostname;
    return (
      <section aria-label={`Approve ${name}`} className="flex flex-col gap-3 rounded-card border border-border bg-surface p-4" data-testid="pair-approve">
        <div className="flex items-start gap-3">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-raised text-fg-subtle ring-1 ring-border" aria-hidden>
            <Laptop className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-ui font-medium text-fg">{name} wants to join</p>
            <p className="text-caption text-fg-subtle">Reported by the computer</p>
          </div>
        </div>
        <PairRequestFacts request={r} />
        {r.differentNetwork && (
          <p className="flex items-center gap-2 text-caption text-status-attention-ink">
            <TriangleAlert className="size-4 shrink-0 text-status-attention" aria-hidden />
            {PAIR_DIFFERENT_NETWORK}
          </p>
        )}
        {pairing.error ? <ErrorState variant="inline" error={pairing.error} /> : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" loading={pairing.busy === 'approve'} disabled={pairing.busy !== null} onClick={() => void pairing.approve()}>
            Approve {name}
          </Button>
          <Button variant="ghost" loading={pairing.busy === 'deny'} disabled={pairing.busy !== null} onClick={() => void pairing.deny()}>
            Deny
          </Button>
        </div>
      </section>
    );
  }
  if (p.kind === 'joining') {
    return (
      <div className="flex items-center gap-3 rounded-card border border-border-subtle px-3 py-3" role="status" aria-live="polite">
        <RowStatus tone="working" icon="working" label={`Approved. Waiting for ${p.request.machine.hostname} to connect`} />
      </div>
    );
  }
  if (p.kind === 'ended') {
    return (
      <div className="flex items-center gap-3 rounded-card border border-border-subtle px-3 py-3" role="status" aria-live="polite">
        <div className="min-w-0 flex-1">
          <RowStatus tone={p.request.state === 'conflict' ? 'attention' : 'neutral'} icon={p.request.state === 'conflict' ? 'warning' : 'stopped'} label={ENDED_LABEL[p.request.state] ?? 'Ended'} />
          <p className="mt-1 text-caption text-fg-subtle">{PAIR_STATE_MESSAGE[p.request.state]}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={pairing.restart}>
          Wait for a new request
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1 rounded-card border border-border-subtle px-3 py-3" role="status" aria-live="polite" data-testid="pair-waiting">
      <RowStatus tone="working" icon="working" label="Waiting for your machine" />
      <p className="pl-6 text-caption text-fg-subtle">
        {byLink
          ? 'It joins with the link: nothing to approve here.'
          : local
          ? reach === 'loopback'
            ? 'Approve it here when it asks to join.'
            : 'Approve it in the browser it opens, or type its words on the page it shows.'
          : reach === 'loopback'
            ? 'Open the downloaded file, then approve this computer here.'
            : 'Open the file on that computer, then approve it in the browser window the installer opened.'}
      </p>
    </div>
  );
}

/** IT deployment: what an administrator needs to allow and roll out the agent, and where the guide is. */
function ItDeployment({ data }: { data: AgentDownloads | null }) {
  const domain = data ? bareDomain(data.server.domain) : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" icon={<Building2 />}>
          IT deployment
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4" aria-label="IT deployment">
        <p className="text-ui font-medium text-fg">IT deployment</p>
        <ul className="mt-2 flex flex-col gap-1 text-caption text-fg-muted">
          <li>Allow-list by publisher{data ? `: ${data.publisher}` : ''}</li>
          <li>Installs per user, without admin rights</li>
          <li>Outbound HTTPS only{domain ? ` to ${domain}` : ''}, no open ports</li>
          <li>Updates are signed and checked before they install</li>
        </ul>
        <p className="mt-3 text-caption text-fg-subtle">
          Guide: <span className="font-mono text-fg">{IT_DEPLOYMENT_DOC}</span>
        </p>
      </PopoverContent>
    </Popover>
  );
}

/** Which tab Add machine opens on: Cloud VM for ?add=cloud, else Computer. */
export type AddMachineTab = 'computer' | 'cloud';
export function addMachineTabFrom(search: string): AddMachineTab {
  return new URLSearchParams(search).get('add') === 'cloud' ? 'cloud' : 'computer';
}

/**
 * Add a machine: download, run, approve (ADR 0042 decision 1). The person downloads the signed
 * agent for their computer (or, on a phone, sends themselves the link), opens it there, and
 * approves the computer here when it asks to join. No command, token or checkout in this
 * journey; the command-line path (the Go agent with a join token) waits behind a disclosure.
 * A console on loopback approves the new computer in place; a remote one never sees the request
 * (ADR 0042) and goes from waiting to connected when the machine reports.
 * `preset` forces a state on demo data (?add=<preset>, screenshots and tests).
 */
export function AddMachineDialog({
  open,
  onOpenChange,
  onOpenMachine,
  preset,
  reach: forcedReach,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onOpenMachine: (id: string) => void;
  preset?: AddMachinePreset;
  /** Demo and tests only: force the loopback or remote path (?console= on Machines). Else from this page's host. */
  reach?: ConsoleReach;
}) {
  const reach = forcedReach ?? consoleReach(window.location.hostname);
  const downloads = useDownloads(open, preset);
  const pairing = usePairing(open, preset, reach);
  const platform = usePlatform(open);
  // ADR 0090: join links land dark, on with ?joinTicket=1 (remembered for the tab).
  const [ticketsOn, setTicketsOn] = useState(() => joinTicketsOn());
  useEffect(() => {
    if (open) setTicketsOn(joinTicketsOn());
  }, [open]);
  // Computer or Cloud VM (ADR 0105): ?add=cloud opens the Cloud VM tab. The Cloud VM tab mints
  // join tickets whatever the flag says: its cloud-init document is built around one.
  const [tab, setTab] = useState<AddMachineTab>(() => addMachineTabFrom(window.location.search));
  useEffect(() => {
    if (open) setTab(addMachineTabFrom(window.location.search));
  }, [open]);
  const join = useJoinTickets(open, ticketsOn || tab === 'cloud');
  const [dev, setDev] = useState(false);
  const [chosen, setChosen] = useState<ReleaseTarget | null>(null);
  useEffect(() => {
    if (open) {
      setDev(preset === 'developer');
      setChosen(null);
    }
  }, [open, preset]);

  const joined = pairing.phase.kind === 'connected' ? pairing.phase.machine : null;
  // The joined machine as it reports now: its readiness arrives with hello or just after (R-ON-4).
  const joinedNow = useOrgus((s) => (joined ? s.machines[joined.id] : undefined)) ?? joined;
  const navigate = useNavigate();
  const data = downloads.state.kind === 'ready' ? downloads.state.data : null;
  // A local hub: the command for the file picked (or this computer's), while nothing is approved yet.
  const pairFile = data?.pairWith && !data.unavailable ? (data.downloads.find((d) => d.target === chosen) ?? pickDownloads(data.downloads, platform).primary) : null;
  const showCommand = !!pairFile && (pairing.phase.kind === 'waiting' || pairing.phase.kind === 'ended');

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={joined ? `${joined.name} connected` : 'Add machine'}
      description={joined ? undefined : tab === 'cloud' ? 'Start a Linux VM in your cloud with the Orgus agent already on it.' : reach === 'loopback' && !(join.link.kind === 'copied' || join.link.kind === 'not-copied') ? 'Install the Orgus agent on the computer you want to add, then approve it here.' : 'Install the Orgus agent on the computer you want to add.'}
      footer={
        joined ? (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                onOpenChange(false);
                onOpenMachine(joined.id);
              }}
            >
              Open {joined.name}
            </Button>
          </>
        ) : (
          <>
            <span className="mr-auto">
              <ItDeployment data={data} />
            </span>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </>
        )
      }
    >
      <AnimatePresence mode="wait" initial={false}>
        {joined ? (
          <motion.div key="joined" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col gap-4">
            <div className="flex items-center gap-3 rounded-card border border-status-success-line bg-status-success-soft p-4" role="status" aria-live="polite" data-testid="pair-connected">
              <CircleCheck className="size-6 shrink-0 text-status-success" aria-hidden />
              <p className="min-w-0 text-ui text-fg-muted">
                <span className="font-medium text-status-success-ink">Reporting now.</span> {OS_LABEL[joined.os]}, {joined.cpus} CPUs. Updates install by themselves.
              </p>
            </div>
            {joinedNow && (
              <ReadinessFound
                machine={joinedNow}
                where={joinedNow.name}
                onAdded={() => {
                  onOpenChange(false);
                  navigate('/getting-started?step=repo');
                }}
              />
            )}
          </motion.div>
        ) : (
          <motion.div key="flow" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Tabs value={tab} onValueChange={(v) => setTab(v === 'cloud' ? 'cloud' : 'computer')}>
            <TabsList aria-label="What to add">
              <TabsTrigger value="computer" icon={<Laptop />} data-testid="add-tab-computer">
                Computer
              </TabsTrigger>
              <TabsTrigger value="cloud" icon={<Cloud />} data-testid="add-tab-cloud">
                Cloud VM
              </TabsTrigger>
            </TabsList>
            <TabsContent value="cloud" className="pt-4">
              <CloudVmTab s={join} {...(data?.pairWith ? { hubUrl: data.pairWith.url } : {})} />
            </TabsContent>
            <TabsContent value="computer" className="flex flex-col gap-4 pt-4">
            {downloads.state.kind === 'loading' ? (
              <div className="flex flex-col gap-3" role="status" aria-label="Loading the downloads">
                <Skeleton className="h-control-lg w-64" />
                <Skeleton className="h-4 w-80" />
              </div>
            ) : downloads.state.kind === 'error' ? (
              <ErrorState variant="inline" error={downloads.state.error} onRetry={downloads.retry} />
            ) : (
              <Downloads data={downloads.state.data} platform={platform} secondary={pairing.phase.kind === 'approve'} onChoose={setChosen} onPrimary={ticketsOn ? () => void join.make() : undefined} />
            )}

            <JoinLinkPanel s={join} />

            {showCommand && pairFile && data?.pairWith && <PairCommand d={pairFile} url={data.pairWith.url} />}

            {/* Waiting says "open the downloaded file": only once there is a file to download. */}
            {(pairing.phase.kind !== 'waiting' || (data && !data.unavailable)) && <Live pairing={pairing} reach={reach} local={!!data?.pairWith} byLink={join.link.kind === 'copied' || join.link.kind === 'not-copied'} />}

            <div className="flex flex-col gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="self-start text-fg-subtle"
                icon={dev ? <ChevronDown /> : <ChevronRight />}
                aria-expanded={dev}
                onClick={() => setDev((v) => !v)}
              >
                Install from the command line
              </Button>
              {dev && data && !data.unavailable && <InstallCommands {...(data.pairWith ? { hubUrl: data.pairWith.url } : {})} />}
              {dev && <DeveloperJoin />}
            </div>
            </TabsContent>
          </Tabs>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
